import React from 'react'
import {
  injectIntl,
  WrappedComponentProps,
  MessageDescriptor
} from 'react-intl'
import { css, cx } from '@emotion/css'
import { OrderDropdown } from './order-dropdown'
import { OrderDirectionButton } from './order-direction-button'
import {
  Order,
  OrderFieldConfig,
  OrderDirection,
  OrderFieldConfigProperty
} from '.'

const containerCss = css`
  display: flex;
  margin-left: auto;
`

const sortByMessage: MessageDescriptor = {
  id: 'title_sortBy',
  defaultMessage: 'Sort By'
}

interface Props<TField extends string> {
  order: Order<TField>
  onSetSortField: (field: TField) => void
  onSetSortDirection: (direction: OrderDirection) => void
  orderFieldConfig: OrderFieldConfig<TField>
  defaultField?: TField
  containerClassName?: string
}

function OrderSelectorBase<TField extends string>({
  order: { direction, field },
  onSetSortField,
  onSetSortDirection,
  orderFieldConfig,
  intl,
  defaultField,
  containerClassName
}: Props<TField> & WrappedComponentProps) {
  const { formatMessage, locale } = intl

  const onDirectionClick = () =>
    onSetSortDirection(direction === 'ASC' ? 'DESC' : 'ASC')

  const supportedItems = Object.keys(orderFieldConfig).filter((k) => {
    const val = orderFieldConfig[k] as OrderFieldConfigProperty
    return !val.supportedLocales || val.supportedLocales.indexOf(locale) >= 0
  })

  const getField = (field: string) => {
    return getFieldOrDefault(field, supportedItems, defaultField)
  }

  return (
    <div
      className={cx(containerCss, !!containerClassName && containerClassName)}
    >
      <OrderDirectionButton onClick={onDirectionClick} direction={direction} />
      <OrderDropdown
        items={supportedItems}
        selectedItem={field}
        itemToString={(field) =>
          formatMessage(orderFieldConfig[getField(field)].message)
        }
        itemSelectionToString={(field) =>
          `${formatMessage(sortByMessage)} ${formatMessage(
            orderFieldConfig[getField(field)].message
          )}`
        }
        itemToKey={(field) => getField(field)}
        onChange={onSetSortField}
        roundedCorners="right"
        unitTestId={'order-selector-id'}
        e2eTestId={'order-selector-sort-dropdown'}
        aria-label="Order By"
      />
    </div>
  )
}

// exported for testing
export function getFieldOrDefault(
  field: string,
  supportedItems: string[],
  defaultField?: string
) {
  if (supportedItems.includes(field)) {
    return field
  } else if (defaultField) {
    return defaultField
  }
  throw new Error('Invalid field selection.')
}

export function OrderSelector<TField extends string>(props: Props<TField>) {
  const WrappedComponent = injectIntl(OrderSelectorBase)
  return <WrappedComponent {...props} />
}
